import { useState, useRef, useEffect, useCallback, useMemo, useContext } from 'react';
import {
    Box,
    Card,
    CardBody,
    HStack,
    Text,
    Spinner,
    Center,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Flex,
    IconButton,
    Tooltip,
    Icon,
    Tag,
    TagLabel,
    TagCloseButton,
    Button,
    VStack,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import { TbDownload, TbMaximize, TbMinimize, TbTablePlus, TbMapPin, TbShieldCheck, TbTruck, TbCar, TbUser } from 'react-icons/tb';
import axios from 'axios';
import { useTableSettings } from '../../../hooks/useTableSettings';
import { useReportingColumnPrefs } from '../../../hooks/useReportingColumnPrefs';
import ReportingFullScreenShell from '../components/ReportingFullScreenShell';
import ReportingTableSettingsDrawer from '../components/ReportingTableSettingsDrawer';
import { columnDrawerRowsToPrefs } from '../components/SettingsColumnsPanel';
import { usePivotStats } from '../../../hooks/reporting/usePivotStats';
import { useReportingFilters } from '../ReportingFiltersContext';
import ExcludeEvsToggle from '../components/ExcludeEvsToggle';
import { PivotDomain, PivotCellMetrics } from '../../../types/reporting';
import SessionContext from '../../../contexts/SessionContext';
import API_URL from '../../../constants/environment';
import type { PopoverOption } from '../components';
import { formatTooltipValue } from '../components/ChartMetricSelector';
import PivotConfigDrawer, { PivotConfigPanel } from '../components/PivotConfigDrawer';
import type { PivotConfig } from '../components/PivotConfigDrawer';
import type { AuditingDrilldownPayload } from '../widgets/WidgetRenderer';

const ZOOM_LEVELS = [50, 60, 70, 80, 90, 100];
const STORAGE_KEY_CONFIG = 'reporting_pivot_config';
const STORAGE_KEY_ZOOM = 'reporting_pivot_zoom';
const TOOLBAR_ICON_BUTTON_SIZE = '32px';

type PivotMetricKey = Exclude<keyof PivotCellMetrics, 'orderIds'>;

const DOMAIN_LABELS: Record<PivotDomain, string> = {
    location: 'Location',
    insurance: 'Insurance',
    vendor: 'Vendor',
    oem: 'OEM',
    estimator: 'Estimator',
};

const DOMAIN_ICONS: Record<PivotDomain, React.ElementType> = {
    location: TbMapPin as React.ElementType,
    insurance: TbShieldCheck as React.ElementType,
    vendor: TbTruck as React.ElementType,
    oem: TbCar as React.ElementType,
    estimator: TbUser as React.ElementType,
};

const PIVOT_METRIC_OPTIONS: (PopoverOption & { format: 'currency' | 'percent' | 'number' })[] = [
    { value: 'orderCount', label: 'Order Count', format: 'number' },
    { value: 'orderVolume', label: 'Orders $', format: 'currency' },
    { value: 'avgOrderSize', label: 'Avg Order Size', format: 'currency' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency' },
    { value: 'avgGrossMarginPct', label: 'Avg Gross Margin', format: 'percent' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent' },
    { value: 'avgGrossMarginPctWoPM', label: 'Gross Margin (w/o PM)', format: 'percent' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency' },
];

const VALID_METRIC_KEYS = new Set(PIVOT_METRIC_OPTIONS.map((o) => o.value));

/** Row highlight (matches reporting accent #0071E3) */
const PIVOT_ROW_SELECTED_BG = 'rgba(0, 113, 227, 0.11)';
const PIVOT_ROW_SELECTED_BG_HOVER = 'rgba(0, 113, 227, 0.16)';
const PIVOT_ROW_SELECTED_BG_SOLID = '#EAECF7';
const PIVOT_ROW_SELECTED_BG_HOVER_SOLID = '#E0E3F3';
const PIVOT_METRIC_COLUMN_WIDTH = '112px';

function getMetricFormat(key: PivotMetricKey): 'currency' | 'percent' | 'number' {
    return PIVOT_METRIC_OPTIONS.find(o => o.value === key)?.format ?? 'number';
}

function formatCellValue(value: number | undefined, key: PivotMetricKey): string {
    if (value === undefined || value === null || isNaN(value)) return '—';
    return formatTooltipValue(value, getMetricFormat(key));
}

function loadPivotConfig(): PivotConfig | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY_CONFIG);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PivotConfig>;
        const validDomains: PivotDomain[] = ['location', 'insurance', 'vendor', 'oem', 'estimator'];
        if (
            validDomains.includes(parsed.primaryDomain as PivotDomain) &&
            validDomains.includes(parsed.secondaryDomain as PivotDomain) &&
            parsed.primaryDomain !== parsed.secondaryDomain
        ) {
            const metric1 =
                typeof parsed.metric1 === 'string' && VALID_METRIC_KEYS.has(parsed.metric1)
                    ? parsed.metric1
                    : 'orderCount';
            const metric2 =
                typeof parsed.metric2 === 'string' && VALID_METRIC_KEYS.has(parsed.metric2)
                    ? parsed.metric2
                    : 'avgGrossMarginPct';
            return {
                primaryDomain: parsed.primaryDomain as PivotDomain,
                secondaryDomain: parsed.secondaryDomain as PivotDomain,
                metric1,
                metric2,
            };
        }
    } catch { /* corrupted storage */ }
    return null;
}

function savePivotConfig(config: PivotConfig) {
    try {
        localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(config));
    } catch {
        /* quota / private mode */
    }
}

function clearPivotConfig() {
    try {
        localStorage.removeItem(STORAGE_KEY_CONFIG);
    } catch {
        /* ignore */
    }
}

function loadZoomPct(): number {
    if (typeof window === 'undefined') return 100;
    try {
        const raw = localStorage.getItem(STORAGE_KEY_ZOOM);
        if (!raw) return 100;
        const n = Number.parseInt(raw, 10);
        if (Number.isNaN(n)) return 100;
        if (ZOOM_LEVELS.includes(n)) return n;
        return ZOOM_LEVELS.reduce((best, z) =>
            Math.abs(z - n) < Math.abs(best - n) ? z : best
        );
    } catch {
        return 100;
    }
}

function saveZoomPct(pct: number) {
    try {
        localStorage.setItem(STORAGE_KEY_ZOOM, String(pct));
    } catch {
        /* ignore */
    }
}

function buildDrilldownForDomain(
    domain: PivotDomain,
    entity: { id: string; name: string },
): Partial<AuditingDrilldownPayload> {
    switch (domain) {
        case 'location':
            return { locationIds: [entity.id] };
        case 'insurance':
            return { insuranceIds: [entity.id] };
        case 'oem':
            return { makes: [entity.name] };
        case 'vendor':
            return { vendors: [entity.name], targetView: 'parts' };
        case 'estimator':
            return {};
    }
}

interface PivotTabProps {
    startDate: string;
    endDate: string;
    granularity: string;
    preset?: string;
    isActive?: boolean;
    onAuditingDrilldown?: (payload: AuditingDrilldownPayload) => void;
}

interface PivotDrilldownSelection {
    domain: PivotDomain;
    id: string;
    name: string;
}

interface PivotDrilldownOrderIdsResponse {
    orderIds: string[];
}

const PivotTab = ({ startDate, endDate, isActive = true, onAuditingDrilldown }: PivotTabProps) => {
    const { excludeEvs } = useReportingFilters();
    const sessionContext = useContext(SessionContext);
    const [pivotConfig, setPivotConfig] = useState<PivotConfig | null>(() => loadPivotConfig());
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [zoomPct, setZoomPct] = useState(() => loadZoomPct());
    const [headerRowHeight, setHeaderRowHeight] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [selectedPivotRowId, setSelectedPivotRowId] = useState<string | null>(null);

    const primaryDomain = pivotConfig?.primaryDomain ?? 'location';
    const secondaryDomain = pivotConfig?.secondaryDomain ?? 'insurance';
    const metric1: PivotMetricKey = (pivotConfig?.metric1 as PivotMetricKey) ?? 'orderCount';
    const metric2: PivotMetricKey = (pivotConfig?.metric2 as PivotMetricKey) ?? 'avgGrossMarginPct';

    useEffect(() => {
        if (!isActive) setIsFullScreen(false);
    }, [isActive]);

    useEffect(() => {
        saveZoomPct(zoomPct);
    }, [zoomPct]);

    useEffect(() => {
        setSelectedPivotRowId(null);
    }, [primaryDomain, secondaryDomain, startDate, endDate]);

    const headerRowRef = useRef<HTMLTableRowElement>(null);

    const togglePivotRowHighlight = useCallback((rowId: string) => {
        setSelectedPivotRowId((prev) => (prev === rowId ? null : rowId));
    }, []);

    const isConfigured = pivotConfig !== null;
    const { data, isLoading } = usePivotStats(
        startDate,
        endDate,
        primaryDomain,
        secondaryDomain,
        isActive && isConfigured,
        excludeEvs,
    );

    const fetchDrilldownOrderIds = useCallback(async (selections: PivotDrilldownSelection[]) => {
        if (!sessionContext?.sessionId || selections.length === 0) return undefined;
        try {
            const { data: response } = await axios.post<PivotDrilldownOrderIdsResponse>(
                `${API_URL}/reporting/pivot/drilldown-order-ids`,
                { startDate, endDate, selections },
                { headers: { Authorization: sessionContext.sessionId } }
            );
            return response.orderIds;
        } catch {
            return undefined;
        }
    }, [sessionContext?.sessionId, startDate, endDate]);

    const triggerAuditingDrilldown = useCallback(async (
        payload: AuditingDrilldownPayload,
        selections: PivotDrilldownSelection[],
    ) => {
        if (!onAuditingDrilldown) return;
        const orderIds = await fetchDrilldownOrderIds(selections);
        const nextPayload: AuditingDrilldownPayload = {
            ...payload,
            ...(orderIds && orderIds.length > 0 ? { orderIds } : {}),
        };
        if (!nextPayload.targetView) {
            nextPayload.targetView = 'orders';
        }
        onAuditingDrilldown(nextPayload);
    }, [onAuditingDrilldown, fetchDrilldownOrderIds]);

    const handleCellDrilldown = useCallback(
        (row: { id: string; name: string }, col?: { id: string; name: string }) => {
            if (!onAuditingDrilldown) return;
            const selections: PivotDrilldownSelection[] = [
                { domain: primaryDomain, id: row.id, name: row.name },
                ...(col ? [{ domain: secondaryDomain, id: col.id, name: col.name }] : []),
            ];
            const payload: AuditingDrilldownPayload = {
                ...buildDrilldownForDomain(primaryDomain, row),
                ...(col ? buildDrilldownForDomain(secondaryDomain, col) : {}),
            };
            void triggerAuditingDrilldown(payload, selections);
        },
        [onAuditingDrilldown, primaryDomain, secondaryDomain, triggerAuditingDrilldown],
    );

    const pivotColIds = useMemo(() => data?.columns.map((c) => c.id) ?? [], [data?.columns]);
    const pivotPrefs = useReportingColumnPrefs(
        `reporting_pivot_cols_${primaryDomain}_${secondaryDomain}`,
        pivotColIds,
        []
    );
    const { settings: tableUiSettings, updateSettings: updateTableUi, tableSettingsSx } = useTableSettings(
        `reporting_pivot_ui_${primaryDomain}_${secondaryDomain}`
    );

    const orderedPivotColumns = useMemo(() => {
        if (!data?.columns?.length) return [];
        const visible = pivotPrefs.visibleOrderedKeys.filter((id) => data.columns.some((c) => c.id === id));
        const ids = visible.length > 0 ? visible : data.columns.map((c) => c.id);
        const byId = new Map(data.columns.map((c) => [c.id, c] as const));
        return ids.map((id) => byId.get(id)).filter(Boolean) as typeof data.columns;
    }, [data, pivotPrefs.visibleOrderedKeys]);

    const pivotColumnDrawerRows = useMemo(
        () =>
            pivotPrefs.columnConfigForDrawer.map((r) => ({
                key: r.key,
                label: data?.columns.find((c) => c.id === r.key)?.name ?? r.key,
                visible: r.visible,
                pinned: r.pinned,
            })),
        [data, pivotPrefs.columnConfigForDrawer]
    );

    const [drawerDraftConfig, setDrawerDraftConfig] = useState<PivotConfig | null>(null);
    const [drawerDraftValid, setDrawerDraftValid] = useState(true);

    const handleDrawerConfigChange = useCallback((config: PivotConfig, valid: boolean) => {
        setDrawerDraftConfig(config);
        setDrawerDraftValid(valid);
    }, []);

    const densityDetailed = tableUiSettings.density === 'detailed';

    const metric1Label = PIVOT_METRIC_OPTIONS.find(o => o.value === metric1)?.label ?? metric1;
    const metric2Label = PIVOT_METRIC_OPTIONS.find(o => o.value === metric2)?.label ?? metric2;
    const primaryLabel = DOMAIN_LABELS[primaryDomain];
    const secondaryLabel = DOMAIN_LABELS[secondaryDomain];

    useEffect(() => {
        const row = headerRowRef.current;
        if (!row) return;

        const updateHeight = () => {
            setHeaderRowHeight(row.getBoundingClientRect().height);
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(row);

        return () => observer.disconnect();
    }, [densityDetailed, metric1Label, metric2Label, orderedPivotColumns.length, primaryLabel, zoomPct]);

    const zoomIn = useCallback(() => {
        setZoomPct(prev => {
            const idx = ZOOM_LEVELS.indexOf(prev);
            return idx < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[idx + 1] : prev;
        });
    }, []);

    const zoomOut = useCallback(() => {
        setZoomPct(prev => {
            const idx = ZOOM_LEVELS.indexOf(prev);
            return idx > 0 ? ZOOM_LEVELS[idx - 1] : prev;
        });
    }, []);

    const handleApplyConfig = useCallback((config: PivotConfig) => {
        setPivotConfig(config);
        savePivotConfig(config);
    }, []);

    const handleClearConfig = useCallback(() => {
        setPivotConfig(null);
        clearPivotConfig();
    }, []);

    const scaleFactor = zoomPct / 100;
    const cellFs = `${Math.max(8, Math.round(12 * scaleFactor))}px`;
    const subFs = `${Math.max(7, Math.round(10 * scaleFactor))}px`;
    const cellPx = `${Math.max(3, Math.round(8 * scaleFactor))}px`;
    const locMinW = `${Math.round(160 * scaleFactor)}px`;

    const handleExportCSV = useCallback(() => {
        if (!data || data.rows.length === 0) return;

        const escapeCell = (val: string) => {
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                return `"${val.replace(/"/g, '""')}"`;
            }
            return val;
        };

        const headers = [primaryLabel];
        for (const col of data.columns) {
            headers.push(`${col.name} - ${metric1Label}`, `${col.name} - ${metric2Label}`);
        }
        headers.push(`Total - ${metric1Label}`, `Total - ${metric2Label}`);

        const rows = data.rows.map(row => {
            const cells: string[] = [row.name];
            for (const col of data.columns) {
                const cell = data.cells[row.id]?.[col.id];
                cells.push(
                    cell?.[metric1] != null ? String(cell[metric1]) : '',
                    cell?.[metric2] != null ? String(cell[metric2]) : '',
                );
            }
            const rt = data.rowTotals[row.id];
            cells.push(
                rt?.[metric1] != null ? String(rt[metric1]) : '',
                rt?.[metric2] != null ? String(rt[metric2]) : '',
            );
            return cells;
        });

        const grandRow: string[] = ['Grand Total'];
        for (const col of data.columns) {
            const ct = data.columnTotals[col.id];
            grandRow.push(
                ct?.[metric1] != null ? String(ct[metric1]) : '',
                ct?.[metric2] != null ? String(ct[metric2]) : '',
            );
        }
        grandRow.push(
            data.grandTotal?.[metric1] != null ? String(data.grandTotal[metric1]) : '',
            data.grandTotal?.[metric2] != null ? String(data.grandTotal[metric2]) : '',
        );
        rows.push(grandRow);

        const csvContent = [
            headers.map(escapeCell).join(','),
            ...rows.map(r => r.map(escapeCell).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `pivot_${primaryDomain}_by_${secondaryDomain}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [data, metric1, metric2, metric1Label, metric2Label, primaryDomain, secondaryDomain, primaryLabel]);

    const tableShellSx = useMemo(
        () => ({
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: '100%',
            height: '100%',
            'th, td': {
                fontSize: cellFs,
                px: cellPx,
                ...(densityDetailed ? { whiteSpace: 'normal' as const } : {}),
            },
            'th[data-sub]': { fontSize: subFs },
            ...tableSettingsSx,
        }),
        [cellFs, cellPx, subFs, densityDetailed, tableSettingsSx]
    );

    const renderDimensionTag = (label: string, domain: PivotDomain, prefix: string) => (
        <Tag
            size="md"
            borderRadius="full"
            variant="outline"
            borderColor="gray.300"
            bg="white"
            px={3}
            py={1.5}
        >
            <Icon as={DOMAIN_ICONS[domain] as React.ElementType} boxSize={3.5} color="gray.500" mr={1.5} />
            <TagLabel fontSize="xs" fontWeight="medium" color="gray.700">
                {prefix}: {label}
            </TagLabel>
            <TagCloseButton
                onClick={(e) => {
                    e.stopPropagation();
                    handleClearConfig();
                }}
            />
        </Tag>
    );

    const renderPivotToolbar = (toolbarMb: number) => (
        <Flex mb={toolbarMb} gap={3} align="center" flexWrap="wrap" flexShrink={0} w="100%">
            <HStack spacing={2}>
                {renderDimensionTag(primaryLabel, primaryDomain, 'Rows')}
                {renderDimensionTag(secondaryLabel, secondaryDomain, 'Columns')}
            </HStack>

            <Box flex={1} />

            <Box minW="280px">
                <ExcludeEvsToggle />
            </Box>

            <HStack spacing={1}>
                <IconButton
                    aria-label="Zoom out"
                    icon={<MinusIcon boxSize={2.5} />}
                    size="xs"
                    variant="outline"
                    borderColor="gray.300"
                    isDisabled={zoomPct <= ZOOM_LEVELS[0]}
                    onClick={zoomOut}
                    h={TOOLBAR_ICON_BUTTON_SIZE}
                    w={TOOLBAR_ICON_BUTTON_SIZE}
                />
                <Text fontSize="xs" color="gray.600" fontWeight="medium" minW="36px" textAlign="center">
                    {zoomPct}%
                </Text>
                <IconButton
                    aria-label="Zoom in"
                    icon={<AddIcon boxSize={2.5} />}
                    size="xs"
                    variant="outline"
                    borderColor="gray.300"
                    isDisabled={zoomPct >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                    onClick={zoomIn}
                    h={TOOLBAR_ICON_BUTTON_SIZE}
                    w={TOOLBAR_ICON_BUTTON_SIZE}
                />
            </HStack>

            <HStack spacing={1} align="flex-end">
                <ReportingTableSettingsDrawer
                    settings={tableUiSettings}
                    onUpdateSettings={updateTableUi}
                    hideColumnDividers
                    triggerButtonProps={{
                        h: TOOLBAR_ICON_BUTTON_SIZE,
                        w: TOOLBAR_ICON_BUTTON_SIZE,
                    }}
                    extraTabs={[
                        {
                            key: 'pivot',
                            label: 'Pivot',
                            content: (
                                <VStack align="stretch" spacing={4}>
                                    <Box bg="gray.50" borderRadius="md" p={3} border="1px solid" borderColor="gray.200">
                                        <Text fontSize="xs" color="gray.500">
                                            Update the row dimension, column dimension, and metrics shown in this pivot table.
                                        </Text>
                                    </Box>
                                    <PivotConfigPanel
                                        currentConfig={pivotConfig}
                                        metricOptions={PIVOT_METRIC_OPTIONS}
                                        onApply={handleApplyConfig}
                                        hideButtons
                                        onConfigChange={handleDrawerConfigChange}
                                    />
                                </VStack>
                            ),
                        },
                    ]}
                    columns={{
                        rows: pivotColumnDrawerRows,
                        onToggle: pivotPrefs.toggleVisible,
                        onReorderDrag: pivotPrefs.reorderByKey,
                        onResetColumns: pivotPrefs.reset,
                        getDefaultRows: () =>
                            (data?.columns ?? []).map((c) => ({
                                key: c.id,
                                label: c.name,
                                visible: true,
                                pinned: false,
                            })),
                    }}
                    onCommitColumns={(rows) => pivotPrefs.setPrefs(columnDrawerRowsToPrefs(rows))}
                    onSaveChanges={() => {
                        if (drawerDraftConfig && drawerDraftValid) {
                            handleApplyConfig(drawerDraftConfig);
                        }
                    }}
                />
                <Tooltip label={isFullScreen ? 'Exit full screen' : 'Full screen table'} placement="top" hasArrow>
                    <IconButton
                        aria-label={isFullScreen ? 'Exit full screen' : 'Full screen table'}
                        icon={<Icon as={(isFullScreen ? TbMinimize : TbMaximize) as React.ElementType} boxSize={4} />}
                        size="sm"
                        h={TOOLBAR_ICON_BUTTON_SIZE}
                        w={TOOLBAR_ICON_BUTTON_SIZE}
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        borderRadius="md"
                        _hover={{ bg: 'gray.50' }}
                        _active={{ bg: 'gray.100' }}
                        onClick={() => setIsFullScreen((v) => !v)}
                    />
                </Tooltip>
                <Tooltip label="Export to CSV" hasArrow placement="top">
                    <IconButton
                        aria-label="Export to CSV"
                        icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                        size="sm"
                        h={TOOLBAR_ICON_BUTTON_SIZE}
                        w={TOOLBAR_ICON_BUTTON_SIZE}
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        borderRadius="md"
                        _hover={{ bg: 'gray.50' }}
                        _active={{ bg: 'gray.100' }}
                        onClick={handleExportCSV}
                        isDisabled={!data || data.rows.length === 0}
                    />
                </Tooltip>
            </HStack>
        </Flex>
    );

    const renderBlankState = () => (
        <Card
            bg="white"
            shadow="none"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
        >
            <CardBody>
                <Center py={20} flexDirection="column" gap={4}>
                    <Icon as={TbTablePlus as React.ElementType} boxSize={10} color="gray.300" />
                    <Text color="gray.400" fontSize="sm" textAlign="center" maxW="320px">
                        Create a pivot table to compare metrics across two dimensions such as Location by Insurance or OEM by Vendor.
                    </Text>
                    <Button
                        leftIcon={<Icon as={TbTablePlus as React.ElementType} boxSize={4} />}
                        size="sm"
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3545A0' }}
                        onClick={() => setDrawerOpen(true)}
                    >
                        Create Pivot Table
                    </Button>
                </Center>
            </CardBody>
        </Card>
    );

    return (
        <>
            <PivotConfigDrawer
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onApply={handleApplyConfig}
                currentConfig={pivotConfig}
                metricOptions={PIVOT_METRIC_OPTIONS}
            />
            <ReportingFullScreenShell
                isOpen={isFullScreen}
                onClose={() => setIsFullScreen(false)}
                fullscreenToolbar={isFullScreen && isConfigured ? renderPivotToolbar(0) : undefined}
            >
            <Box
                w="100%"
                h="100%"
                flex={1}
                minW={0}
                minH={0}
                display="flex"
                flexDirection="column"
                overflow="hidden"
            >
                {!isConfigured ? (
                    renderBlankState()
                ) : (
                    <>
                        {!isFullScreen && renderPivotToolbar(4)}

                        <Box
                            w="100%"
                            flex={1}
                            minW={0}
                            minH={0}
                            overflowX="auto"
                            overflowY="auto"
                            bg="white"
                            {...(!isFullScreen && {
                                border: '1px solid',
                                borderColor: 'gray.200',
                                borderRadius: 'lg',
                            })}
                            sx={{
                                overscrollBehavior: 'contain',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 transparent',
                                '& thead': {
                                    zIndex: '3 !important',
                                },
                            }}
                        >
                                {isLoading ? (
                                    <Center py={16}>
                                        <Spinner size="lg" color="#0071E3" />
                                    </Center>
                                ) : !data || data.rows.length === 0 ? (
                                    <Center py={16}>
                                        <Text color="gray.400" fontSize="sm">No data for the selected period.</Text>
                                    </Center>
                                ) : (
                                    <>
                                        <Table size="sm" variant="simple" sx={tableShellSx}>
                                            <Thead>
                                                <Tr ref={headerRowRef}>
                                                    <Th
                                                        position="sticky"
                                                        left={0}
                                                        top={0}
                                                        bg="gray.50"
                                                        zIndex={4}
                                                        borderBottom="none"
                                                        borderRight="2px solid"
                                                        borderRightColor="gray.400"
                                                        minW={locMinW}
                                                        rowSpan={2}
                                                        verticalAlign="bottom"
                                                        fontSize="xs"
                                                        color="gray.600"
                                                        boxShadow="inset 0 -1px 0 var(--chakra-colors-gray-200)"
                                                    >
                                                        {primaryLabel}
                                                    </Th>
                                                    {orderedPivotColumns.map((col, colIdx) => {
                                                        const isFirstPivotCol = colIdx === 0;
                                                        const isLastPivotCol = colIdx === orderedPivotColumns.length - 1;
                                                        return (
                                                            <Th
                                                                key={col.id}
                                                                colSpan={2}
                                                                textAlign="center"
                                                                position="sticky"
                                                                top={0}
                                                                bg="gray.50"
                                                                zIndex={3}
                                                                {...(isFirstPivotCol
                                                                    ? {}
                                                                    : { borderLeft: '2px solid', borderLeftColor: 'gray.400' })}
                                                                {...(isLastPivotCol ? { borderRight: 'none' } : {})}
                                                                borderBottom="1px solid"
                                                                borderBottomColor="gray.200"
                                                                fontSize="xs"
                                                                color="gray.700"
                                                                fontWeight="semibold"
                                                                whiteSpace={densityDetailed ? 'normal' : 'nowrap'}
                                                                px={2}
                                                                cursor={onAuditingDrilldown ? 'pointer' : undefined}
                                                                onClick={onAuditingDrilldown ? () => {
                                                                    void triggerAuditingDrilldown(
                                                                        {
                                                                            ...buildDrilldownForDomain(secondaryDomain, col),
                                                                            targetView: secondaryDomain === 'vendor' ? 'parts' : 'orders',
                                                                        },
                                                                        [{ domain: secondaryDomain, id: col.id, name: col.name }],
                                                                    );
                                                                } : undefined}
                                                                _hover={onAuditingDrilldown ? { textDecoration: 'underline', color: '#0071E3' } : undefined}
                                                            >
                                                                {col.name}
                                                            </Th>
                                                        );
                                                    })}
                                                    <Th
                                                        colSpan={2}
                                                        textAlign="center"
                                                        position="sticky"
                                                        top={0}
                                                        bg="gray.100"
                                                        zIndex={3}
                                                        borderLeft="2px solid"
                                                        borderLeftColor="gray.600"
                                                        borderRight="none"
                                                        borderBottom="1px solid"
                                                        borderBottomColor="gray.200"
                                                        fontSize="xs"
                                                        color="gray.700"
                                                        fontWeight="bold"
                                                        whiteSpace="nowrap"
                                                    >
                                                        Total
                                                    </Th>
                                                </Tr>
                                                <Tr>
                                                    {orderedPivotColumns.map((col, colIdx) => (
                                                        <MetricHeaders
                                                            key={col.id}
                                                            metric1Label={metric1Label}
                                                            metric2Label={metric2Label}
                                                            isTotal={false}
                                                            isFirstPivotGroup={colIdx === 0}
                                                            isLastPivotGroup={colIdx === orderedPivotColumns.length - 1}
                                                            stickyTop={headerRowHeight}
                                                            densityDetailed={densityDetailed}
                                                        />
                                                    ))}
                                                    <MetricHeaders
                                                        metric1Label={metric1Label}
                                                        metric2Label={metric2Label}
                                                        isTotal
                                                        isFirstPivotGroup={false}
                                                        isLastPivotGroup={false}
                                                        stickyTop={headerRowHeight}
                                                        densityDetailed={densityDetailed}
                                                    />
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {data.rows.map((row) => {
                                                    const rowSelected = selectedPivotRowId === row.id;
                                                    return (
                                                        <Tr
                                                            key={row.id}
                                                            cursor="pointer"
                                                            tabIndex={0}
                                                            aria-selected={rowSelected}
                                                            data-highlight-row={rowSelected ? 'true' : undefined}
                                                            onClick={() => togglePivotRowHighlight(row.id)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    togglePivotRowHighlight(row.id);
                                                                }
                                                            }}
                                                            sx={{
                                                                '& > td': {
                                                                    transition: 'background-color 0.12s ease',
                                                                },
                                                                '&[data-highlight-row="true"] > td': {
                                                                    bg: PIVOT_ROW_SELECTED_BG,
                                                                },
                                                                '&:hover > td': {
                                                                    bg: rowSelected ? PIVOT_ROW_SELECTED_BG_HOVER : 'gray.50',
                                                                },
                                                                '&:focus-visible': {
                                                                    outline: '2px solid #0071E3',
                                                                    outlineOffset: '2px',
                                                                },
                                                            }}
                                                        >
                                                            <Td
                                                                position="sticky"
                                                                left={0}
                                                                bg={rowSelected ? PIVOT_ROW_SELECTED_BG_SOLID : 'white'}
                                                                zIndex={1}
                                                                borderBottom="1px solid"
                                                                borderBottomColor="gray.100"
                                                                borderRight="2px solid"
                                                                borderRightColor="gray.400"
                                                                fontWeight="medium"
                                                                fontSize="xs"
                                                                whiteSpace={densityDetailed ? 'normal' : 'nowrap'}
                                                                cursor={onAuditingDrilldown ? 'pointer' : undefined}
                                                                onClick={(e) => {
                                                                    if (onAuditingDrilldown) {
                                                                        e.stopPropagation();
                                                                        handleCellDrilldown(row);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    'tr:hover &': {
                                                                        bg: rowSelected ? PIVOT_ROW_SELECTED_BG_HOVER_SOLID : 'gray.50',
                                                                    },
                                                                    ...(onAuditingDrilldown ? {
                                                                        '&:hover': {
                                                                            textDecoration: 'underline',
                                                                            color: '#0071E3',
                                                                        },
                                                                    } : {}),
                                                                }}
                                                            >
                                                                {row.name}
                                                            </Td>
                                                            {orderedPivotColumns.map((col, colIdx) => {
                                                                const cell = data.cells[row.id]?.[col.id];
                                                                return (
                                                                    <MetricCells
                                                                        key={col.id}
                                                                        cell={cell}
                                                                        metric1={metric1}
                                                                        metric2={metric2}
                                                                        isTotal={false}
                                                                        isFirstPivotGroup={colIdx === 0}
                                                                        isLastPivotGroup={colIdx === orderedPivotColumns.length - 1}
                                                                        rowHighlighted={rowSelected}
                                                                        densityDetailed={densityDetailed}
                                                                        onDrilldown={onAuditingDrilldown ? () => handleCellDrilldown(row, col) : undefined}
                                                                    />
                                                                );
                                                            })}
                                                            <MetricCells
                                                                cell={data.rowTotals[row.id]}
                                                                metric1={metric1}
                                                                metric2={metric2}
                                                                isTotal
                                                                rowHighlighted={rowSelected}
                                                                densityDetailed={densityDetailed}
                                                                onDrilldown={onAuditingDrilldown ? () => handleCellDrilldown(row) : undefined}
                                                            />
                                                        </Tr>
                                                    );
                                                })}
                                                <Tr bg="gray.50" fontWeight="bold">
                                                    <Td
                                                        position="sticky"
                                                        left={0}
                                                        bg="gray.100"
                                                        zIndex={1}
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                        borderTop="1px solid"
                                                        borderTopColor="gray.200"
                                                        borderRight="2px solid"
                                                        borderRightColor="gray.400"
                                                    >
                                                        Grand Total
                                                    </Td>
                                                    {orderedPivotColumns.map((col, colIdx) => (
                                                        <MetricCells
                                                            key={col.id}
                                                            cell={data.columnTotals[col.id]}
                                                            metric1={metric1}
                                                            metric2={metric2}
                                                            isTotal={false}
                                                            isTotalRow
                                                            isFirstPivotGroup={colIdx === 0}
                                                            isLastPivotGroup={colIdx === orderedPivotColumns.length - 1}
                                                            densityDetailed={densityDetailed}
                                                            onDrilldown={onAuditingDrilldown ? () => {
                                                                void triggerAuditingDrilldown(
                                                                    {
                                                                        ...buildDrilldownForDomain(secondaryDomain, col),
                                                                        targetView: secondaryDomain === 'vendor' ? 'parts' : 'orders',
                                                                    },
                                                                    [{ domain: secondaryDomain, id: col.id, name: col.name }],
                                                                );
                                                            } : undefined}
                                                        />
                                                    ))}
                                                    <MetricCells
                                                        cell={data.grandTotal}
                                                        metric1={metric1}
                                                        metric2={metric2}
                                                        isTotal
                                                        isTotalRow
                                                        densityDetailed={densityDetailed}
                                                    />
                                                </Tr>
                                            </Tbody>
                                        </Table>
                                        <Box px={4} py={2} borderTop="1px solid" borderColor="gray.100">
                                            <Text fontSize="xs" color="gray.400">
                                                {primaryLabel} vs {secondaryLabel} &mdash; showing {metric1Label} and {metric2Label}
                                            </Text>
                                        </Box>
                                    </>
                                )}
                        </Box>
                    </>
                )}
            </Box>
            </ReportingFullScreenShell>
        </>
    );
};

function MetricHeaders({
    metric1Label,
    metric2Label,
    isTotal,
    isFirstPivotGroup,
    isLastPivotGroup,
    stickyTop,
    densityDetailed,
}: {
    metric1Label: string;
    metric2Label: string;
    isTotal: boolean;
    /** When true, omit 2px left on first metric — row-label column already draws that edge */
    isFirstPivotGroup: boolean;
    /** When true, omit right edge on second metric — Total column (or table end) draws the next edge */
    isLastPivotGroup: boolean;
    stickyTop: number;
    densityDetailed: boolean;
}) {
    const bg = isTotal ? 'gray.100' : 'gray.50';
    const ws = densityDetailed ? 'normal' : 'nowrap';
    const groupBorder =
        isTotal
            ? { borderLeft: '2px solid', borderLeftColor: 'gray.600' }
            : isFirstPivotGroup
                ? {}
                : { borderLeft: '2px solid', borderLeftColor: 'gray.400' };
    const innerBorder = isTotal
        ? { borderLeft: '1px solid', borderLeftColor: 'gray.400' }
        : { borderLeft: '1px solid', borderLeftColor: 'gray.300' };
    /** One side only at boundaries: no double line before Total or at table end */
    const metric2Outer =
        (!isTotal && isLastPivotGroup) || isTotal ? { borderRight: 'none' as const } : {};
    return (
        <>
            <Th
                data-sub=""
                textAlign="right"
                color="gray.500"
                fontWeight="medium"
                whiteSpace={ws}
                w={PIVOT_METRIC_COLUMN_WIDTH}
                minW={PIVOT_METRIC_COLUMN_WIDTH}
                py={1}
                textTransform="none"
                letterSpacing="normal"
                position="sticky"
                top={`${stickyTop}px`}
                zIndex={3}
                bg={bg}
                borderBottom="none"
                boxShadow="inset 0 -1px 0 var(--chakra-colors-gray-200)"
                {...groupBorder}
            >
                {metric1Label}
            </Th>
            <Th
                data-sub=""
                textAlign="right"
                color="gray.500"
                fontWeight="medium"
                whiteSpace={ws}
                w={PIVOT_METRIC_COLUMN_WIDTH}
                minW={PIVOT_METRIC_COLUMN_WIDTH}
                py={1}
                textTransform="none"
                letterSpacing="normal"
                position="sticky"
                top={`${stickyTop}px`}
                zIndex={3}
                bg={bg}
                borderBottom="none"
                boxShadow="inset 0 -1px 0 var(--chakra-colors-gray-200)"
                {...innerBorder}
                {...metric2Outer}
            >
                {metric2Label}
            </Th>
        </>
    );
}

function MetricCells({
    cell,
    metric1,
    metric2,
    isTotal,
    isTotalRow = false,
    rowHighlighted = false,
    isFirstPivotGroup = false,
    isLastPivotGroup = false,
    densityDetailed,
    onDrilldown,
}: {
    cell: PivotCellMetrics | undefined;
    metric1: PivotMetricKey;
    metric2: PivotMetricKey;
    isTotal: boolean;
    isTotalRow?: boolean;
    /** When set, omit default row-total gray bg so parent row highlight shows through */
    rowHighlighted?: boolean;
    isFirstPivotGroup?: boolean;
    isLastPivotGroup?: boolean;
    densityDetailed: boolean;
    onDrilldown?: () => void;
}) {
    const groupBorder =
        isTotal
                ? { borderLeft: '2px solid', borderLeftColor: 'gray.600' }
                : isFirstPivotGroup
                    ? {}
                    : { borderLeft: '2px solid', borderLeftColor: 'gray.400' };
    const innerBorder = isTotal
        ? { borderLeft: '1px solid', borderLeftColor: 'gray.400' }
        : { borderLeft: '1px solid', borderLeftColor: 'gray.300' };
    const metric2Outer =
        (!isTotal && isLastPivotGroup) || isTotal ? { borderRight: 'none' as const } : {};

    const bgProps = isTotal && isTotalRow
        ? { bg: 'gray.100' }
        : isTotal && rowHighlighted
            ? {}
            : isTotal
                ? { bg: 'gray.50' }
                : isTotalRow
                    ? { bg: 'gray.50' }
                    : {};
    const fontProps = isTotalRow ? { fontWeight: 'bold' as const } : {};
    const topBorder = isTotalRow ? { borderTop: '1px solid', borderTopColor: 'gray.200' } : {};
    const ws = densityDetailed ? 'normal' : 'nowrap';

    const clickProps = onDrilldown
        ? {
              cursor: 'pointer' as const,
              onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDrilldown();
              },
              _hover: {
                  textDecoration: 'underline',
                  color: '#0071E3',
              },
          }
        : {};

    return (
        <>
            <Td
                textAlign="right"
                py={1.5}
                whiteSpace={ws}
                w={PIVOT_METRIC_COLUMN_WIDTH}
                minW={PIVOT_METRIC_COLUMN_WIDTH}
                {...groupBorder}
                {...bgProps}
                {...fontProps}
                {...topBorder}
                {...clickProps}
            >
                {cell ? formatCellValue(cell[metric1], metric1) : '—'}
            </Td>
            <Td
                textAlign="right"
                py={1.5}
                whiteSpace={ws}
                w={PIVOT_METRIC_COLUMN_WIDTH}
                minW={PIVOT_METRIC_COLUMN_WIDTH}
                color={isTotal ? undefined : 'gray.600'}
                {...innerBorder}
                {...metric2Outer}
                {...bgProps}
                {...fontProps}
                {...topBorder}
                {...clickProps}
            >
                {cell ? formatCellValue(cell[metric2], metric2) : '—'}
            </Td>
        </>
    );
}

export default PivotTab;
